import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  Clock,
  KeyRound,
  LogIn,
  MapPinned,
  Bot,
  MessageCircle,
  Plus,
  RotateCw,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { formatMoney, money, ZERO_MONEY } from '@aeris/money'
import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  RowActions,
  RowOpener,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  VisuallyHidden,
} from '@aeris/ui'

import { CredentialHandover } from '../components/credential-handover.tsx'
import { PlatformAccountDialog } from '../components/platform-account-dialog.tsx'
import { PlatformAccountForm } from '../components/platform-account-form.tsx'
import {
  STATUS_LABELS,
  StatusBadge,
} from '../components/platform-account-status.tsx'
import { PlatformAssistant } from '../components/platform-assistant.tsx'
import { PlatformMaps } from '../components/platform-maps.tsx'
import { PlatformMessagingServers } from '../components/platform-messaging-servers.tsx'
import {
  fetchPlatformAccounts,
  reactivatePlatformAccount,
  refreshPlatformAccountUsage,
  resetPlatformOwnerPassword,
  startPlatformSupport,
  suspendPlatformAccount,
  type PlatformAccountType,
  type ResetOwnerPasswordResponseType,
} from '../lib/api/platform.ts'
import { ApiError } from '../lib/api.ts'
import { formatDate } from '../lib/date-format.ts'

/**
 * As contas da instalação — a tela de quem vende o Aeris (F119).
 *
 * Fora do sistema de módulos de propósito, como `/trocar-senha` e `/servidor`:
 * uma entrada em `PAGES` sem permissão apareceria no menu de todo caixa de toda
 * loja, e uma COM permissão exigiria inventar uma permissão de loja para uma
 * tela que não é de loja. O portão é `RequirePlatformAdmin`, no router, e a API
 * recusa por conta própria — esconder o caminho nunca foi trancar a porta.
 *
 * Não há ação em massa. Suspender quatro lojas com um clique errado é caro
 * demais, e o ganho seria conveniência para quem faz isso uma vez por mês.
 */

const PAGE_SIZE = 25

/** `R$ 1.234,56`, e `R$ 0,00` quando o valor não é um número reconhecível. */
const asMoney = (wire: string): string => {
  try {
    return formatMoney(money(wire))
  } catch {
    return formatMoney(ZERO_MONEY)
  }
}

export const PlataformaPage = (): ReactNode => {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)
  const [creating, setCreating] = useState(false)
  /** Os servidores de WhatsApp (F192) — da instalação, não de uma conta. */
  const [serversOpen, setServersOpen] = useState(false)
  const [assistantOpen, setAssistantOpen] = useState(false)
  /** A chave do Google das Rotas de entrega (F265). */
  const [mapsOpen, setMapsOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** A ficha de leitura (F154): clicar na linha, sem tocar em nada. */
  const [viewing, setViewing] = useState<PlatformAccountType | null>(null)
  const [suspending, setSuspending] = useState<PlatformAccountType | null>(null)
  /** A conta cuja senha do dono está prestes a ser redefinida (confirmação). */
  const [resetting, setResetting] = useState<PlatformAccountType | null>(null)
  /** A conta em que se está prestes a entrar como suporte (F169). */
  const [entering, setEntering] = useState<PlatformAccountType | null>(null)
  /** A senha recém-gerada, na tela uma única vez. */
  const [newCredential, setNewCredential] =
    useState<ResetOwnerPasswordResponseType | null>(null)

  const query = useQuery({
    queryKey: ['platform-accounts', search, status, page],
    queryFn: async ({ signal }) =>
      await fetchPlatformAccounts(
        {
          page,
          pageSize: PAGE_SIZE,
          ...(search === '' ? {} : { search }),
          ...(status === ''
            ? {}
            : { status: status as PlatformAccountType['status'] }),
        },
        signal
      ),
    placeholderData: (previous) => previous,
  })

  const report = (cause: unknown): void => {
    setError(
      cause instanceof ApiError
        ? cause.message
        : 'Não foi possível concluir. Tente novamente.'
    )
  }

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['platform-accounts'] })
  }

  const suspend = useMutation({
    mutationFn: async (input: { tenantId: string; reason: string }) =>
      await suspendPlatformAccount(input.tenantId, input.reason),
    onSuccess: invalidate,
    onError: report,
  })

  const reactivate = useMutation({
    mutationFn: async (tenantId: string) =>
      await reactivatePlatformAccount(tenantId),
    onSuccess: invalidate,
    onError: report,
  })

  const measure = useMutation({
    mutationFn: async (tenantId: string) =>
      await refreshPlatformAccountUsage(tenantId),
    onSuccess: invalidate,
    onError: report,
  })

  /*
   * Não invalida a lista: nada que a lista mostra muda com uma redefinição de
   * senha. Recarregar aqui só piscaria a tabela embaixo do diálogo que está
   * mostrando a senha — e piscar embaixo de uma senha que aparece uma vez é
   * exatamente a hora errada.
   */
  const resetOwner = useMutation({
    mutationFn: async (tenantId: string) =>
      await resetPlatformOwnerPassword(tenantId),
    onSuccess: (credential) => {
      setNewCredential(credential)
    },
    onError: report,
  })

  /**
   * Entrar na loja de um cliente (F169).
   *
   * Não invalida a lista, e não fecha diálogo nenhum: depois desta chamada a
   * SESSÃO inteira está dentro da conta do cliente, e o console deixa de
   * responder para ela. Recarregar a sessão é o que troca a tela — o roteador
   * leva para o Início da loja porque `/plataforma` deixou de ser alcançável.
   */
  const enterSupport = useMutation({
    mutationFn: async (input: { tenantId: string; reason: string }) =>
      await startPlatformSupport(input.tenantId, input.reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries()
    },
    onError: report,
  })

  const accounts = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const filtered = search.trim().length > 0 || status !== ''

  return (
    <Page>
      <PageHeader
        title="Contas"
        description="As lojas desta instalação do Aeris One."
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={
                <MessageCircle aria-hidden="true" className="h-4 w-4" />
              }
              onClick={() => {
                setServersOpen(true)
              }}
            >
              Servidores de WhatsApp
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Bot aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setAssistantOpen(true)
              }}
            >
              Assistente do Aeris
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<MapPinned aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setMapsOpen(true)
              }}
            >
              Mapa
            </Button>
            <Button
              variant="primary"
              size="sm"
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setError(null)
                setCreating(true)
              }}
            >
              Abrir conta
            </Button>
          </>
        }
      />

      <ListToolbar>
        <SearchInput
          placeholder="Buscar por nome, identificador ou CNPJ"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
          aria-label="Buscar contas"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value)
            setPage(1)
          }}
          aria-label="Situação"
          className="w-44"
        >
          <option value="">Todas as situações</option>
          <option value="active">{STATUS_LABELS.active}</option>
          <option value="suspended">{STATUS_LABELS.suspended}</option>
          <option value="archived">{STATUS_LABELS.archived}</option>
        </Select>
      </ListToolbar>

      {error === null ? null : <InlineAlert tone="danger">{error}</InlineAlert>}

      <ResultCount
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        loading={query.isPending}
        none="Nenhuma conta"
      />

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="as contas"
        isEmpty={accounts.length === 0}
        filtered={filtered}
        onClearFilters={() => {
          setSearch('')
          setStatus('')
          setPage(1)
        }}
        empty={
          <EmptyState
            icon={<Building2 aria-hidden="true" className="h-8 w-8" />}
            title="Nenhuma conta ainda"
            description="Abra a primeira conta para começar."
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Conta</TableHeaderCell>
                <TableHeaderCell>CNPJ</TableHeaderCell>
                <TableHeaderCell>Dono</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <TableHeaderCell>Pessoas</TableHeaderCell>
                <TableHeaderCell>Produtos</TableHeaderCell>
                <TableHeaderCell>Vendas no mês</TableHeaderCell>
                <TableHeaderCell>Medido em</TableHeaderCell>
                <TableHeaderCell>
                  <VisuallyHidden>Ações</VisuallyHidden>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.map((account) => (
                /* Clicar na linha abre a FICHA (F154): razão social, nome
                   fantasia, desde quando, quantas pessoas ao todo e o custo de
                   comunicação — tudo já carregado e invisível na tabela. */
                <TableRow
                  key={account.tenantId}
                  onOpen={() => {
                    setViewing(account)
                  }}
                >
                  <TableCell>
                    <RowOpener
                      onOpen={() => {
                        setViewing(account)
                      }}
                    >
                      <span className="block text-(--text-primary)">
                        {account.name}
                      </span>
                    </RowOpener>
                    <span className="block font-(--font-mono) text-(length:--text-xs) text-(--text-muted)">
                      {account.slug}
                    </span>
                  </TableCell>
                  <TableCell>{account.cnpj ?? '—'}</TableCell>
                  <TableCell>{account.ownerEmail ?? '—'}</TableCell>
                  <TableCell>
                    <StatusBadge status={account.status} />
                  </TableCell>
                  <TableCell>{account.usage?.activeUserCount ?? '—'}</TableCell>
                  <TableCell>{account.usage?.productCount ?? '—'}</TableCell>
                  <TableCell>
                    {account.usage === null ? (
                      '—'
                    ) : (
                      <>
                        <span className="block">
                          {asMoney(account.usage.salesAmount)}
                        </span>
                        <span className="block text-(length:--text-xs) text-(--text-muted)">
                          {`${String(account.usage.salesCount)} ${
                            account.usage.salesCount === 1 ? 'venda' : 'vendas'
                          }`}
                        </span>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {/*
                      "Ainda não medido", e nunca zero.
                      Zero é uma afirmação — a de que a loja não tem nada —, e
                      ela é falsa sobre uma conta que nasceu hoje e que ninguém
                      contou. Um número sem data é um número que o leitor supõe
                      ser de agora.
                    */}
                    {account.usage === null ? (
                      <span className="text-(--text-muted)">
                        ainda não medido
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-(length:--text-xs) text-(--text-muted)">
                        <Clock aria-hidden="true" className="h-3 w-3" />
                        {formatDate(account.usage.capturedAt)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <RowActions
                      name={account.name}
                      primary={
                        <Button
                          variant="ghost"
                          size="sm"
                          leadingIcon={
                            <RotateCw
                              aria-hidden="true"
                              className="h-3.5 w-3.5"
                            />
                          }
                          loading={
                            measure.isPending &&
                            measure.variables === account.tenantId
                          }
                          onClick={() => {
                            setError(null)
                            measure.mutate(account.tenantId)
                          }}
                        >
                          Medir
                        </Button>
                      }
                      items={[
                        /*
                         * Redefinir a senha do dono (F121). Vale para conta
                         * suspensa também: quem está negociando a volta de um
                         * cliente inadimplente precisa que ele consiga entrar
                         * para ver o próprio faturamento.
                         */
                        account.status === 'active' && {
                          label: 'Ver como esta loja',
                          icon: (
                            <LogIn aria-hidden="true" className="h-4 w-4" />
                          ),
                          onSelect: () => {
                            setError(null)
                            setEntering(account)
                          },
                        },
                        {
                          label: 'Redefinir senha do dono',
                          icon: (
                            <KeyRound aria-hidden="true" className="h-4 w-4" />
                          ),
                          disabled: account.ownerEmail === null,
                          onSelect: () => {
                            setError(null)
                            setResetting(account)
                          },
                        },
                        account.status === 'suspended' && {
                          label: 'Reativar',
                          disabled: reactivate.isPending,
                          onSelect: () => {
                            setError(null)
                            reactivate.mutate(account.tenantId)
                          },
                        },
                        account.status === 'active' && {
                          label: 'Suspender',
                          danger: true,
                          onSelect: () => {
                            setError(null)
                            setSuspending(account)
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      {assistantOpen ? (
        <PlatformAssistant
          onClose={() => {
            setAssistantOpen(false)
          }}
        />
      ) : null}

      {mapsOpen ? (
        <PlatformMaps
          onClose={() => {
            setMapsOpen(false)
          }}
        />
      ) : null}

      {serversOpen ? (
        <PlatformMessagingServers
          onClose={() => {
            setServersOpen(false)
          }}
        />
      ) : null}

      {creating && (
        <PlatformAccountForm
          onClose={() => {
            setCreating(false)
          }}
        />
      )}

      {suspending !== null && (
        <ConfirmDialog
          open
          title={`Suspender ${suspending.name}?`}
          description="A loja perde o acesso agora. Quem estiver com o Aeris aberto é desconectado, e a conta volta quando você reativar."
          confirmLabel="Suspender"
          tone="danger"
          reason={{
            label: 'Motivo',
            placeholder: 'Inadimplência de dois meses',
            // Obrigatório: a linha de auditoria precisa responder "por que essa
            // loja parou?" daqui a seis meses, e quem for responder já não vai
            // lembrar. O servidor também recusa sem motivo.
            required: true,
          }}
          onConfirm={(reason) => {
            suspend.mutate({
              tenantId: suspending.tenantId,
              reason: reason ?? '',
            })
            setSuspending(null)
          }}
          onOpenChange={(next) => {
            if (!next) setSuspending(null)
          }}
        />
      )}

      {entering !== null && (
        <ConfirmDialog
          open
          title={`Entrar em ${entering.name}?`}
          description="Você passa a agir dentro desta loja com poder de dono, por 30 minutos. Tudo o que fizer fica gravado no seu nome, e o lojista não é avisado. O console some do menu até você sair."
          confirmLabel="Entrar"
          reason={{
            label: 'Motivo',
            placeholder: 'O Eduardo não consegue imprimir a etiqueta',
            /*
             * Obrigatório, e o servidor também recusa sem. Esta frase é a
             * ÚNICA coisa que vai responder, daqui a um ano, por que a Aeris
             * esteve dentro da loja deste cliente — porque ele não foi
             * avisado, por decisão registrada no adendo da ADR-0035.
             */
            required: true,
          }}
          onConfirm={(reason) => {
            enterSupport.mutate({
              tenantId: entering.tenantId,
              reason: reason ?? '',
            })
            setEntering(null)
          }}
          onOpenChange={(next) => {
            if (!next) setEntering(null)
          }}
        />
      )}

      {resetting !== null && (
        <ConfirmDialog
          open
          title={`Redefinir a senha de ${resetting.ownerEmail ?? 'quem é dono'}?`}
          description="A senha atual para de funcionar agora e quem estiver com o Aeris aberto é desconectado. A nova senha aparece uma única vez, e só você poderá entregá-la."
          confirmLabel="Redefinir"
          tone="danger"
          onConfirm={() => {
            resetOwner.mutate(resetting.tenantId)
            setResetting(null)
          }}
          onOpenChange={(next) => {
            if (!next) setResetting(null)
          }}
        />
      )}

      {newCredential !== null && (
        <Dialog
          open
          onOpenChange={(next) => {
            /*
             * Não fecha clicando fora enquanto a senha está na tela — mesma
             * regra do diálogo de criação, e pelo mesmo motivo: um clique
             * distraído aqui custa outra redefinição e outra conversa com o
             * lojista.
             */
            if (!next) return
          }}
        >
          <DialogContent title="Senha temporária">
            <CredentialHandover
              intro="Entregue-a a quem é dono desta conta."
              email={newCredential.ownerEmail}
              password={newCredential.temporaryPassword}
              onClose={() => {
                setNewCredential(null)
              }}
            />
          </DialogContent>
        </Dialog>
      )}
      {viewing === null ? null : (
        <PlatformAccountDialog
          account={viewing}
          onClose={() => {
            setViewing(null)
          }}
        />
      )}
    </Page>
  )
}
